'use client';

import { useCallback, useEffect, useState } from 'react';
import { getText } from '@/utils/text/text';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useDebounce } from '@/hooks/use-debounce';
import './styles/@theme';

const LAYOUT_DEBOUNCE_MS = 150;
const VIRTUAL_GAMES_LIST_SELECTOR = '.virtual-games-grid__container';

export const BackToTopButton = () => {
    const [show, setShow] = useState(false);
    const { isMobile } = useGlobalContext();

    const updateShow = useCallback(() => {
        const docElem = document.documentElement;
        const clientHeight = docElem.clientHeight;
        const gamesList = document.querySelector<HTMLElement>(VIRTUAL_GAMES_LIST_SELECTOR);
        const contentHeight = gamesList ? gamesList.offsetHeight : docElem.scrollHeight;
        setShow(contentHeight >= 2 * clientHeight && !isMobile);
    }, [isMobile]);

    const debouncedUpdateShow = useDebounce(updateShow, LAYOUT_DEBOUNCE_MS);

    useEffect(() => {
        const documentElement = document.documentElement;
        updateShow();
        const resizeObserver = new ResizeObserver(debouncedUpdateShow);
        resizeObserver.observe(documentElement);
        const gamesList = document.querySelector<HTMLElement>(VIRTUAL_GAMES_LIST_SELECTOR);
        if (gamesList) {
            resizeObserver.observe(gamesList);
        }
        window.addEventListener('scroll', debouncedUpdateShow, { passive: true });
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', debouncedUpdateShow);
        }
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('scroll', debouncedUpdateShow);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', debouncedUpdateShow);
            }
        };
    }, [debouncedUpdateShow, updateShow]);

    const jumpToTop = () => {
        window.scrollTo({
            top: 0
        });
    };

    return (
        show && (
            <button type="button" onClick={jumpToTop} className="backtotop-button">
                {getText({ text: 'common.backToTop' })}
            </button>
        )
    );
};
